import { each, isNil } from "lodash";
import moment from "moment";
import validate from "validate.js";
import documentTypes from "../documents/documentTypes";
import { convertForNumberValidation } from "./operations";

validate.extend(validate.validators.datetime, {
    // The value is guaranteed not to be null or undefined but otherwise it
    // could be anything.
    parse: function (value, options) {
        return +moment.utc(value);
    },
    // Input is a unix timestamp
    format: function (value, options) {
        var format = options.dateOnly ? "YYYY-MM-DD" : "YYYY-MM-DD hh:mm:ss";
        console.log(moment.utc(value).format(format));
        return moment.utc(value).format(format);
    },
});

validate.validators.idregex = function (value, options, key, attributes) {
    const valid = options.allowEmpty ? isNil(value) || idPattern.test(value) : idPattern.test(value);
    return valid ? null : options.message;
};

validate.validators.numberRegex = function (value, options, key, attributes) {
    if (value === "") value = null;
    const valid = options.allowEmpty
        ? isNil(value) || numericPattern.test(convertForNumberValidation(value))
        : numericPattern.test(convertForNumberValidation(value));
    return valid ? null : options.message;
};

validate.validators.decimalRegex = (value, options) => {
    if (value === "") value = null;
    const valid = options.allowEmpty ? isNil(value) || decimalPattern.test(value) : decimalPattern.test(value);
    return valid ? null : options.message;
};

validate.validators.dateregex = function (value, options, key, attributes) {
    const valid = options.allowEmpty ? isNil(value) || datePattern.test(value) : datePattern.test(value);
    return valid ? null : options.message;
};

validate.extend(validate.validators.numericality, {
    parse: function (value, options) {
        value = value || null;
        return value;
    },
});

validate.validators.integerRegex = (value, options) => {
    if (value === "") value = null;
    const valid = options.allowEmpty ? isNil(value) || intPattern.test(value) : intPattern.test(value);
    return valid ? null : options.message;
};

validate.validators.wholeNumber = (value, options) => {
    if (value === "") value = null;
    if (options.allowEmpty && isNil(value)) return null;
    const valid = intPattern.test(value);
    return valid && value >= 0 ? null : options.message;
};

validate.validators.positiveNumberOrEmpty = (value, options) => {
    const validEmptyValues = [null, undefined, ""];
    if (validEmptyValues.includes(value)) return null;
    const numericVal = parseInt(value);
    return numericVal >= 0 ? null : options.message;
};

validate.validators.interval = (value, options) => {
    const validEmptyValues = [null, undefined, ""];
    if (validEmptyValues.includes(value)) return null;
    const numericVal = Number(value);
    return numericVal >= options.greaterThanOrEqualTo && numericVal <= options.lessThanOrEqualTo
        ? null
        : options.message;
};

validate.validators.maxLength = (value, options) => {
    const validEmptyValues = [null, undefined, ""];
    if (validEmptyValues.includes(value)) return null;
    const length = String(value).trim().length;
    return length <= options.lessThanOrEqualTo ? null : options.message;
};

const datePattern =
    /^(19|20\d{2}(-|\/)\d{1,2}(-|\/)(\d{1,2}|\d{2}))(T?\d{2}?:?\d{2}?:?\d{2}?)?|^(\d{1,2}(-|\/)\d{1,2}(-|\/)(((19|20)\d{2})|(\d{2}))( \d{2}:\d{2}:\d{2} \w{1,})?)$/; // For datepicker
const decimalPattern = /^(0(\.\d+)?|1(\.0+)?)$/; // Matches decimal numbers between 0.0 and 1.0
const idPattern = /^$|^"\d+"$|^(\d+)$/;
const intPattern = /^(\+|-)?\d+$/;
const numericPattern = /^-?\d+(\.\d{1,2})?$/;
const yearPattern = /\d{4}/;

export const constraints = {
    required: {
        presence: { allowEmpty: false, message: "Required" },
    },
    number: {
        numberRegex: {
            allowEmpty: true,
            message: "Field must be a number",
        },
    },
    positiveNumber: {
        numericality: {
            greaterThanOrEqualTo: 0,
            message: "Field must be a number greater than or equal to zero",
        },
    },
    positiveNumberOrEmpty: {
        positiveNumberOrEmpty: {
            message: "Field must be a positive number",
        },
    },
    between1And12: {
        interval: {
            greaterThanOrEqualTo: 1,
            lessThanOrEqualTo: 12,
            message: "Invalid integer",
        },
    },
    between0And100: {
        interval: {
            greaterThanOrEqualTo: 0,
            lessThanOrEqualTo: 100,
            message: "Invalid integer",
        },
    },
    between0And1000: {
        interval: {
            greaterThanOrEqualTo: 0,
            lessThanOrEqualTo: 999,
            message: "Field cannot be greater than 1000",
        },
    },
    date: {
        dateregex: {
            allowEmpty: true,
            message: "Invalid date format",
        },
    },
    id: {
        idregex: {
            allowEmpty: true,
            message: "Invalid",
        },
    },
    year: {
        format: {
            pattern: yearPattern,
            flags: "i",
            message: "Invalid year",
        },
    },
    integer: {
        integerRegex: {
            allowEmpty: true,
            message: "Field must be an integer",
        },
    },
    wholeNumber: {
        wholeNumber: {
            allowEmpty: true,
            message: "Field must be a whole number",
        },
    },
    decimal: {
        decimalRegex: {
            allowEmpty: true,
            message: "Field must be a decimal value between 0.0 and 1.0",
        },
    },
    maxLength: {
        maxLength: {
            lessThanOrEqualTo: 2000,
            message: "2000 character limit exceeded.",
        },
    },
};

export const validateRequiredDocuments = (documentState, formType) => {
    if (documentState) {
        let valid = true;
        const savedDocuments = documentState.savedDocuments;
        const requiredTypes = Object.values(documentTypes[formType]).filter((docType) => {
            return docType.required;
        });
        each(Object.keys(requiredTypes), (key) => {
            const isSavedDocumentType = Object.values(savedDocuments).some(
                (document) => document?.documentType?.name === requiredTypes[key].display
            );
            if (!isSavedDocumentType) {
                valid = false;
            }
        });
        return valid ? {} : { documents: "required" };
    }
    return {};
};
